import {DoorCadModelKey} from '@shared/models/door_cad_registry';

import {createMapStore} from '@shared_web/lib/map_data_store';

import {apiCall} from '@mavismart_frontend/lib/api';

/**
 * Where a door's 3D model can be fetched from.
 *
 * `'none'` is a first-class value: most doors have no CAD model, and that is not an error.
 */
export type DoorCadModelState =
  | {kind: 'none'}
  | {kind: 'error'; message: string}
  | {
      kind: 'model';
      modelKey: DoorCadModelKey;
      label: string;
      modelUrl: string;
      /** When the URL is signed, the moment it stops working. */
      expiresAt?: number;
    };

const doorCadStore = createMapStore<string, DoorCadModelState>();

export const useDoorCadModel = doorCadStore.useData;

/** Doors currently being fetched, so a re-render cannot fire the request twice. */
const inFlight = new Set<string>();

/**
 * Fetches the model descriptor for a door.
 *
 * Called only when the 3D section becomes visible: the URL is short-lived, so asking for it before
 * it is needed would risk handing the viewer an already-expired URL.
 */
export function fetchDoorCadModel(doorId: string): void {
  if (inFlight.has(doorId)) {
    return;
  }
  const existing = doorCadStore.getData(doorId);
  if (existing?.kind === 'model' && !isExpired(existing)) {
    return;
  }
  inFlight.add(doorId);
  apiCall('POST /get-door-3d-model', {doorId})
    .then(({model}) => {
      if (model === undefined) {
        doorCadStore.setData(doorId, {kind: 'none'});
        return;
      }
      const state: DoorCadModelState = {
        kind: 'model',
        modelKey: model.modelKey,
        label: model.label,
        modelUrl: model.modelUrl,
      };
      if (model.expiresInSeconds !== undefined) {
        state.expiresAt = Date.now() + model.expiresInSeconds * 1000;
      }
      doorCadStore.setData(doorId, state);
    })
    .catch((err: unknown) => {
      doorCadStore.setData(doorId, {
        kind: 'error',
        message: err instanceof Error ? err.message : String(err),
      });
    })
    .finally(() => {
      inFlight.delete(doorId);
    });
}

function isExpired(state: DoorCadModelState): boolean {
  if (state.kind !== 'model' || state.expiresAt === undefined) {
    return false;
  }
  // Treat a URL as expired slightly early: a download that starts just before expiry still works,
  // but one that starts after does not.
  return Date.now() > state.expiresAt - 60_000;
}

export function clearDoorCadModels(): void {
  doorCadStore.batchUnsetData([...doorCadStore.getAllData().keys()]);
}
